import * as React from 'react';
import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';

import { withStyles } from '@material-ui/core/styles';
import {
    Button, Typography, CircularProgress, TextField, NoSsr,
} from '@material-ui/core';

import classnames from 'classnames';
import * as AuthStore from '../../../app/store/AuthStore';
import urls from '../../../app/urls';
import {
    validateAllFields, validateField, validateRequired, validateEmail,
} from '../../../youwell-common/validationUtils';

const styles = theme => ({
    root: {
        padding: theme.spacing(3),
        background: theme.colors.grey1,
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        marginBottom: theme.spacing(3),
    },
    textField: {
        marginBottom: theme.spacing(2),
    },
    actionButtons: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: theme.spacing(3),
    },
    infoContainer: {
        marginTop: theme.spacing(3),
    },
    loading: {
        height: 340,
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
    hidden: {
        display: 'none',
    },
});

const defaultState = {
    school: '',
    address: '',
    name: '',
    email: '',
    phone: '',
};

const validators = {
    school: validateRequired,
    address: validateRequired,
    name: validateRequired,
    email: [validateEmail, validateRequired],
    phone: validateRequired,
};

class RegisterSchool extends React.Component {
    state = {
        ...defaultState,
        validationErrors: {},
    };

    handleChange = name => (event) => {
        this.setState({ [name]: event.target.value }, () => {
            if (validators[name]) {
                const validationErrors = validateField(this.state, validators[name], name); // eslint-disable-line react/no-access-state-in-setstate
                this.setState({ validationErrors });
            }
        });
    };

    registerSchool = () => {
        const validationErrors = validateAllFields(this.state, validators);

        if (!validationErrors.hasErrors) {
            const registerData = {
                name: this.state.school,
                contactInfo: {
                    name: this.state.name,
                    email: this.state.email,
                    phone: this.state.phone,
                    address: this.state.address,
                },
            };

            this.props.registerSchool(registerData, { redirect: urls.inside.home });
        } else {
            this.setState({ validationErrors });
        }
    };

    render() {
        const {
            classes, translate, errorMessage, loading, history,
        } = this.props;

        return (
            <div className={classes.root}>
                <div className={classes.header}>
                    <Typography variant="h6">
                        {translate('register.school')}
                    </Typography>
                </div>

                {loading ? (
                    <div className={classes.loading}>
                        <CircularProgress />
                    </div>
                ) : (
                    <>
                        <TextField
                            id="school"
                            label={translate('register.label.school')}
                            className={classes.textField}
                            value={this.state.school}
                            onChange={this.handleChange('school')}
                            fullWidth
                            InputLabelProps={{
                                shrink: true,
                            }}
                            error={!!this.state.validationErrors.school}
                            helperText={this.state.validationErrors.school ? translate(this.state.validationErrors.school) : null}
                        />
                        <TextField
                            id="address"
                            label={translate('register.label.address')}
                            className={classes.textField}
                            value={this.state.address}
                            onChange={this.handleChange('address')}
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={4}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            error={!!this.state.validationErrors.address}
                            helperText={this.state.validationErrors.address ? translate(this.state.validationErrors.address) : null}
                        />
                        <Typography className={classes.textField}>
                            {translate('register.contactInformation')}
                        </Typography>
                        <TextField
                            id="name"
                            type="name"
                            label={translate('register.label.name')}
                            className={classnames(classes.textField, { [classes.hidden]: this.state.parent })}
                            value={this.state.name}
                            onChange={this.handleChange('name')}
                            fullWidth
                            InputLabelProps={{
                                shrink: true,
                            }}
                            error={!!this.state.validationErrors.name}
                            helperText={this.state.validationErrors.name ? translate(this.state.validationErrors.name) : null}
                        />
                        <TextField
                            id="email"
                            type="email"
                            label={translate('register.label.email')}
                            className={classes.textField}
                            value={this.state.email}
                            onChange={this.handleChange('email')}
                            fullWidth
                            InputLabelProps={{
                                shrink: true,
                            }}
                            error={!!this.state.validationErrors.email}
                            helperText={this.state.validationErrors.email ? translate(this.state.validationErrors.email) : null}
                        />
                        <TextField
                            id="phone"
                            type="phone"
                            label={translate('register.label.phone')}
                            className={classnames(classes.textField, { [classes.hidden]: this.state.parent })}
                            value={this.state.phone}
                            onChange={this.handleChange('phone')}
                            fullWidth
                            InputLabelProps={{
                                shrink: true,
                            }}
                            error={!!this.state.validationErrors.phone}
                            helperText={this.state.validationErrors.phone ? translate(this.state.validationErrors.phone) : null}
                        />
                        <div className={classes.actionButtons}>
                            <Button onClick={() => history.goBack()} color="primary">
                                {translate('register.back')}
                            </Button>
                            <Button onClick={this.registerSchool} variant="contained" color="primary" disabled={loading}>
                                {translate('register.sendRegistration')}
                            </Button>
                        </div>
                        <div className={classes.infoContainer}>
                            {errorMessage && (
                                <NoSsr>
                                    <Typography className={classes.error} color="error" align="center">
                                        {translate(`errors.${errorMessage}`, null, { onMissingTranslation: ({ translationId }) => translationId })}
                                    </Typography>
                                </NoSsr>
                            )}
                        </div>
                    </>
                )}
            </div>
        );
    }
}

RegisterSchool.propTypes = {
    classes: PropTypes.object.isRequired,
    translate: PropTypes.func.isRequired,
    registerSchool: PropTypes.func.isRequired,
    history: PropTypes.object.isRequired,
    loading: PropTypes.bool,
    errorMessage: PropTypes.string,
};

RegisterSchool.defaultProps = {
    loading: false,
    errorMessage: null,
};

const mapStateToProps = state => ({
    translate: getTranslate(state.localize),
    loading: state.auth.loading,
    errorMessage: [].concat(state.auth.errors).filter(val => val).join('<br/>'),
});

const mapDispatchToProps = dispatch => ({
    registerSchool: bindActionCreators(AuthStore.actionCreators, dispatch).registerOrganization,
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(RegisterSchool)));
